/**
 * The stat-block pill (1.AC-2 deliverable 8, `header.pills.statblock`): a route link to the form,
 * "Stat block · CR <rating>" for the primary sheet or "Add stat block" without one. It renders
 * nothing unless `statblock_form` is on and the document is canon with a `has_sheet` type. A route
 * link, not a document link: a plain click navigates.
 */
import { flagsFromMe, type SlotComponentProps } from '@codex/contracts';
import { Link } from '@tanstack/react-router';
import { useShellSession } from '../shell/session.js';
import { useSheetDoc, useSheets } from './data/hooks.js';

const FRACTIONS: Readonly<Record<number, string>> = { 0.125: '1/8', 0.25: '1/4', 0.5: '1/2' };

/** "1/8", "1/4", "1/2" or the whole number. */
export function ratingText(rating: number): string {
  return FRACTIONS[rating] ?? String(rating);
}

export function StatBlockPill({ docId }: SlotComponentProps) {
  const me = useShellSession();
  const on = me !== null && flagsFromMe(me).statblock_form;
  const sd = useSheetDoc(docId, on);
  const shown =
    on &&
    sd.doc !== undefined &&
    sd.doc.library === 'canon' &&
    sd.doc.deletedAt === null &&
    sd.type?.capabilities.includes('has_sheet') === true;
  const sheets = useSheets(docId, shown);
  if (!shown || !sheets.data) return null;
  const primary = sheets.data.find((sheet) => sheet.isPrimary);
  return (
    <Link
      to="/sheets/$docId"
      params={{ docId }}
      className="cx-button cx-button--secondary cx-button--sm"
      data-testid="sheets.form.pill"
    >
      <span className="cx-button__label">
        {primary
          ? `Stat block · CR ${ratingText(primary.data.challenge.rating)}`
          : 'Add stat block'}
      </span>
    </Link>
  );
}
